import { useInfiniteQuery, useQuery } from '@tanstack/react-query'
import { Button, Card, Flex, Segmented, Space, Typography } from 'antd'
import { Plus } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  organizationsRolesListOptions,
  permissionsListInfiniteOptions,
  type OrganizationRoleFull,
  type PermissionsLocalized
} from 'src/api/main'
import { Loading } from 'src/components'

import { THIRD_PARTY_CUSTOM_APP_TYPE } from '../../constants/constants'

import {
  CRM_INSTANCE_TYPE,
  CRM_PERMISSION_MODULE_LABELS,
  CRM_PERMISSION_MODULE_ORDER
} from './constants'
import { DeleteRoleDrawer } from './DeleteRoleDrawer'
import { PermissionsCatalogPanel } from './PermissionsCatalogPanel'
import { RoleEditorDrawer } from './RoleEditorDrawer'
import { RolesPermissionsTable } from './RolesPermissionsTable'
import { ThirdPartyRolesMatrix } from './ThirdPartyRolesMatrix'
import { groupPermissionsByResource } from './utils'

const PER_PAGE_MAX = 100

type Props = {
  applicationId: number
  applicationType?: string
  applicationRepositoryId?: number
  organizationId: number
}

type RolesView = 'matrix' | 'catalog'

export const AppRolesMatrixPanel = ({
  applicationId,
  applicationType,
  applicationRepositoryId,
  organizationId
}: Props) => {
  const { t } = useTranslation()
  // With 100+ permissions the matrix and the catalog don't fit one page;
  // third-party apps (the only ones with an editable catalog) get a switcher.
  const [view, setView] = useState<RolesView>('matrix')
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingRole, setEditingRole] = useState<OrganizationRoleFull | null>(
    null
  )
  const [deletingRole, setDeletingRole] = useState<OrganizationRoleFull | null>(
    null
  )

  const isThirdParty = applicationType === THIRD_PARTY_CUSTOM_APP_TYPE

  const permissionsQuery = isThirdParty
    ? { application: applicationId, per_page: PER_PAGE_MAX }
    : {
        application_repository: applicationRepositoryId,
        per_page: PER_PAGE_MAX
      }

  const {
    data: permissionsData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: isLoadingPermissions
  } = useInfiniteQuery({
    ...permissionsListInfiniteOptions({
      query: permissionsQuery
    }),
    getNextPageParam: ({ next }) => next,
    enabled: isThirdParty ? !!applicationId : !!applicationRepositoryId
  })

  useEffect(() => {
    if (!hasNextPage || isFetchingNextPage) return
    void fetchNextPage()
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  const { data: rolesData, isLoading: isLoadingRoles } = useQuery({
    ...organizationsRolesListOptions({
      query: {
        application: applicationId,
        organization: organizationId,
        per_page: PER_PAGE_MAX
      }
    }),
    enabled: !!applicationId && !!organizationId
  })

  const permissions = useMemo<PermissionsLocalized[]>(
    () => permissionsData?.pages?.flatMap((p) => p.results ?? []) ?? [],
    [permissionsData?.pages]
  )

  // Third-party apps render the binary ThirdPartyRolesMatrix instead, which
  // takes the flat list — resource grouping only applies to platform apps.
  const groupedPermissions = useMemo(
    () => (isThirdParty ? {} : groupPermissionsByResource(permissions)),
    [isThirdParty, permissions]
  )

  const roles = useMemo<OrganizationRoleFull[]>(
    () => rolesData?.results ?? [],
    [rolesData]
  )

  const isLoading = isLoadingPermissions || isLoadingRoles

  if (isLoading) {
    return <Loading />
  }

  const isCrm = applicationType === CRM_INSTANCE_TYPE

  const handleOpenCreate = () => {
    setEditingRole(null)
    setEditorOpen(true)
  }

  const handleEditRole = (role: OrganizationRoleFull) => {
    setEditingRole(role)
    setEditorOpen(true)
  }

  const handleDeleteRole = (role: OrganizationRoleFull) => {
    if (role.is_admin) return
    setDeletingRole(role)
  }

  const showMatrix = !isThirdParty || view === 'matrix'

  return (
    <Flex vertical gap={14}>
      {isThirdParty ? (
        <Segmented
          value={view}
          onChange={(v) => setView(v as RolesView)}
          style={{ alignSelf: 'flex-start' }}
          options={[
            {
              label: t('workspace.apps.roles.matrixTitle'),
              value: 'matrix'
            },
            {
              label: t('workspace.apps.roles.permissionsCatalogTitle'),
              value: 'catalog'
            }
          ]}
        />
      ) : null}

      {showMatrix ? (
        <>
          <Card size="small" variant="outlined">
            <Flex justify="space-between" align="center" gap={8}>
              <Space vertical size={2}>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  {t('workspace.apps.roles.matrixTitle')}
                </Typography.Title>
                <Typography.Text type="secondary">
                  {t('workspace.apps.roles.panelIntro')}
                </Typography.Text>
              </Space>
              <Button
                type="primary"
                icon={<Plus size={16} />}
                onClick={handleOpenCreate}
              >
                {t('workspace.apps.roles.add')}
              </Button>
            </Flex>
          </Card>

          {isThirdParty ? (
            <ThirdPartyRolesMatrix
              permissions={permissions}
              roles={roles}
              onEditRole={handleEditRole}
              onDeleteRole={handleDeleteRole}
            />
          ) : (
            <RolesPermissionsTable
              groupedPermissions={groupedPermissions}
              roles={roles}
              applicationType={applicationType}
              applicationId={applicationId}
              organizationId={organizationId}
              moduleOrder={isCrm ? CRM_PERMISSION_MODULE_ORDER : undefined}
              moduleLabels={isCrm ? CRM_PERMISSION_MODULE_LABELS : undefined}
              onEditRole={handleEditRole}
              onDeleteRole={handleDeleteRole}
            />
          )}
        </>
      ) : (
        <PermissionsCatalogPanel
          applicationId={applicationId}
          permissions={permissions}
          roles={roles}
        />
      )}

      <RoleEditorDrawer
        open={editorOpen}
        role={editingRole}
        applicationId={applicationId}
        organizationId={organizationId}
        onClose={() => {
          setEditorOpen(false)
          setEditingRole(null)
        }}
      />

      <DeleteRoleDrawer
        open={Boolean(deletingRole)}
        role={deletingRole}
        applicationId={applicationId}
        organizationId={organizationId}
        onClose={() => setDeletingRole(null)}
      />
    </Flex>
  )
}
